import {createI18n} from 'vue-i18n';
import {
  getLocale,
  loadComponentsLocales,
  setComponentsLocale,
} from '@hit/components/src/plugins/i18n/i18n';
import {
  applyI18nLocale,
  loadLocaleMessagesFromGlob,
  mergeLocaleMessages,
  normalizeI18nLocale,
} from '../../../../../i18n-glob.js';
import enGBMessages from '../../locales/en-GB.json';

const FALLBACK_LOCALE = 'en-GB';

// Variable pour stocker l'instance i18n externe
let externalI18n = null;

// Fonction pour configurer l'instance i18n externe
export const setExternalI18n = (i18nInstance) => {
  externalI18n = i18nInstance;
};

export const getExternalI18n = () => externalI18n;

/** Lazy loaders — JSON is fetched only when a locale is requested. */
const baseLocaleModules = import.meta.glob('../../locales/**/*.json');
const componentsLocaleModules = import.meta.glob(
  '../../../../hit-components/src/locales/**/*.json',
);
const loadedLocales = new Set([FALLBACK_LOCALE]);

const europeDateFormat = {
  longSeconds: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  },
  long: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  short: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  },
};

const numberFormats = {
  'en-GB': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'fr-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'de-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'nl-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
};

const datetimeFormats = {
  'fr-BE': europeDateFormat,
  'nl-BE': europeDateFormat,
  'de-BE': europeDateFormat,
  'en-GB': europeDateFormat,
};

export const i18n = createI18n({
  locale: getLocale(),
  allowComposition: true,
  globalInjection: true,
  legacy: false,
  fallbackLocale: {
    fr: ['fr-BE'],
    de: ['de-BE'],
    nl: ['nl-BE'],
    en: ['en-GB'],
    default: ['en-GB'],
  },
  numberFormats,
  datetimeFormats,
  messages: {
    [FALLBACK_LOCALE]: enGBMessages,
  },
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false,
});

/**
 * Load hit-base (+ hit-components) locale messages into this package's i18n instance.
 * Used when no external app i18n is wired yet.
 */
export async function loadBaseLocales(...locales) {
  const toLoad = [
    ...new Set(
      locales
        .filter(Boolean)
        .map((l) => normalizeI18nLocale(l))
        .filter((l) => !loadedLocales.has(l)),
    ),
  ];
  if (!toLoad.length) {
    return;
  }

  const [baseMessages, componentsMessages] = await Promise.all([
    loadLocaleMessagesFromGlob(baseLocaleModules, toLoad),
    loadLocaleMessagesFromGlob(componentsLocaleModules, toLoad),
    loadComponentsLocales(...toLoad),
  ]);

  toLoad.forEach((locale) => {
    const merged = {};
    if (baseMessages[locale]) {
      merged[locale] = {...baseMessages[locale]};
    }
    mergeLocaleMessages(merged, componentsMessages);
    if (merged[locale]) {
      i18n.global.setLocaleMessage(locale, merged[locale]);
    }
    loadedLocales.add(locale);
  });
}

/**
 * Ensure fallback + requested locale are loaded, then activate the locale.
 */
export async function setBaseLocale(locale) {
  const normalized = normalizeI18nLocale(locale);
  await Promise.all([
    loadBaseLocales(FALLBACK_LOCALE, normalized),
    setComponentsLocale(normalized),
  ]);
  applyI18nLocale(i18n, normalized);
}

export const $n = i18n.global.n;
export const $d = i18n.global.d;

export const translate = (key, payload) => {
  if (!key) {
    return '';
  }
  if (externalI18n) {
    return externalI18n.global.t(key, payload);
  }

  return i18n.global.t(key, payload);
};

export const formatDate = (date, format) => {
  if (!date) {
    return '';
  }
  return $d(date, format);
};

export default function installVueI18N(app) {
  setBaseLocale(getLocale());
  app.use(i18n);
}
